import React, { useState, useCallback, useRef, useEffect } from 'react';

import { Box, Typography, TextField, IconButton, Button } from '@mui/material';
import useDeepCompareEffect from 'use-deep-compare-effect';

import {
	IQuestionData,
	ISectionBlockProps,
	ISectionDataWithoutQuestions,
} from 'src/features/questionnaire_template/domain/types';
import {
	generateUniqueId,
	isFrontendId,
} from 'src/features/questionnaire_template/service/questionnaireTemplateService';
import { titleToSnakeCase } from 'src/utils/formatter';

import { QuestionBlock } from './QuestionBlock';

export const SectionBlock: React.FC<ISectionBlockProps> = ({
	id,
	onDelete,
	onChange,
	defaultData,
	validation,
}) => {
	const [sectionData, setSectionData] =
		useState<ISectionDataWithoutQuestions>(
			defaultData || {
				name: '',
				display_name: '',
				is_hidden: false,
			},
		);
	const [questions, setQuestions] = useState<(string | number)[]>(
		Object.keys(defaultData?.questions || {}),
	);
	const [questionDataMap, setQuestionDataMap] = useState<
		Record<string, IQuestionData>
	>(defaultData?.questions || {});

	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const enableOnChangeUseEffectRef_1 = useRef<boolean>(false);
	const enableOnChangeUseEffectRef_2 = useRef<boolean>(false);
	const enableDefaultDataChangeUseEffectRef = useRef<boolean>(false);

	const handleAddQuestion = () => {
		const newId = generateUniqueId('q');
		setQuestions((prev) => [...prev, newId]);
	};

	const handleQuestionChange = useCallback(
		(questionId: number | string, questionData: IQuestionData) => {
			setQuestionDataMap((prev) => ({
				...prev,
				[questionId]: questionData,
			}));
		},
		[],
	);

	const handleDeleteQuestion = useCallback((questionId: string | number) => {
		setQuestions((prev) => {
			const updated = prev.filter((qid) => qid !== questionId);

			if (updated.length === 0 && sectionData.is_hidden) {
				onDelete(id);
			}

			return updated;
		});

		setQuestionDataMap((prev) => {
			const updatedQuestions = { ...prev };

			if (isFrontendId(questionId)) {
				delete updatedQuestions[questionId];
			} else {
				if (updatedQuestions[questionId]) {
					updatedQuestions[questionId] = {
						...updatedQuestions[questionId],
						is_deleted: true,
					};
				}
			}

			return updatedQuestions;
		});
	}, []);

	useDeepCompareEffect(() => {
		if (!enableOnChangeUseEffectRef_1.current) {
			enableOnChangeUseEffectRef_1.current = true;
			return;
		}

		if (debounceRef.current) clearTimeout(debounceRef.current);

		debounceRef.current = setTimeout(() => {
			onChange(id, {
				...sectionData,
				questions: questionDataMap,
			});
		}, 300);

		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, [sectionData, id, onChange]);

	useDeepCompareEffect(() => {
		if (!enableOnChangeUseEffectRef_2.current) {
			enableOnChangeUseEffectRef_2.current = true;
			return;
		}

		onChange(id, {
			...sectionData,
			questions: questionDataMap,
		});
	}, [questionDataMap, id, onChange]);

	useEffect(() => {
		if (!enableDefaultDataChangeUseEffectRef.current) {
			enableDefaultDataChangeUseEffectRef.current = true;
			return;
		}

		if (defaultData) {
			const { questions, ...sectionDataWithoutQuestions } = defaultData;

			setSectionData(sectionDataWithoutQuestions);
			setQuestionDataMap(questions);
			setQuestions(Object.keys(questions || {}));
		}
	}, [defaultData]);

	if (sectionData.is_deleted) return null;

	if (sectionData.is_hidden)
		return (
			<>
				{questions.map((id) => (
					<QuestionBlock
						key={id}
						id={id}
						onDelete={handleDeleteQuestion}
						onChange={handleQuestionChange}
						defaultData={questionDataMap[id]}
						validation={validation?.questions?.[id]}
					/>
				))}
			</>
		);

	return (
		<Box
			sx={{
				position: 'relative',
				backgroundColor: 'white',
				p: 3,
				borderRadius: '6px',
				border: '2px dashed #E53326',
				mt: 2,
			}}
		>
			<Typography
				sx={{
					position: 'absolute',
					top: '-12px',
					left: '12px',
					backgroundColor: 'white',
					px: 1,
					fontSize: '14px',
					color: '#E53326',
				}}
			>
				Section Block
			</Typography>

			<TextField
				label='Questionnaire Section Title'
				variant='outlined'
				fullWidth
				value={sectionData?.display_name}
				onChange={(e) =>
					setSectionData((prev) => ({
						...prev,
						display_name: e.target.value,
						name: titleToSnakeCase(e.target.value),
						is_updated: true,
					}))
				}
				sx={{
					'& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
						{
							borderColor: '#BE3327',
						},
					'& .MuiInputLabel-root.Mui-focused': {
						color: '#BE3327',
					},
				}}
				error={!!validation?.display_name}
				helperText={validation?.display_name}
			/>

			<Box
				sx={{
					display: 'flex',
					justifyContent: 'flex-end',
					alignItems: 'center',
					mt: 2,
					gap: 2,
				}}
			>
				<IconButton onClick={() => onDelete(id)}>
					<img
						src='/delete.svg'
						alt='delete'
						width={16}
						height={18}
					/>
				</IconButton>
			</Box>

			{/* Question Blocks */}
			{questions.map((id) => (
				<QuestionBlock
					key={id}
					id={id}
					onDelete={handleDeleteQuestion}
					onChange={handleQuestionChange}
					defaultData={questionDataMap[id]}
					validation={validation?.questions?.[id]}
				/>
			))}

			<Box
				sx={{
					display: 'flex',
					justifyContent: 'flex-end',
					mt: 2,
				}}
			>
				<Button
					variant='contained'
					sx={{ backgroundColor: '#00000099' }}
					onClick={handleAddQuestion}
				>
					Add Question
				</Button>
			</Box>
		</Box>
	);
};
